/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { SOL_MINT, PLATFORM_FEE_BPS } from './solana';
import { JUPITER_TOKEN_LIST_URL } from '../constants';

export interface JupiterQuoteRequest {
  inputMint: string;
  outputMint: string;
  amount: number; // in atomic units
  slippageBps?: number;
}

export interface JupiterQuoteResponse {
  inputMint: string;
  inAmount: string;
  outputMint: string;
  outAmount: string;
  otherAmountThreshold: string;
  swapMode: 'ExactIn' | 'ExactOut';
  slippageBps: number;
  priceImpactPct: number;
  routePlan: any[];
  platformFee?: {
    amount: string;
    feeBps: number;
  };
}

export interface JupiterToken {
    address: string;
    chainId: number;
    decimals: number;
    name: string;
    symbol: string;
    logoURI: string;
    tags: string[];
}

/**
 * Simulates fetching a quote from Jupiter API v6
 */
export const getJupiterQuote = async (params: JupiterQuoteRequest): Promise<JupiterQuoteResponse> => {
  // Simulate network latency for quote
  await new Promise(r => setTimeout(r, 600));

  // Determine mock exchange rate
  let rate = 1;
  let impact = 0.1;

  if (params.inputMint === SOL_MINT) {
      // Buy: SOL -> Token
      // Simulate rate based on a generic token price of ~$0.001 per token vs $145 SOL
      rate = 145000; // 1 SOL = 145,000 Tokens (Mock)
      // High volume impact simulation
      if (params.amount > 1_000_000_000) impact = 1.5; // > 1 SOL
  } else {
      // Sell: Token -> SOL
      rate = 0.0000069; // 1 Token = 0.0000069 SOL
      if (params.amount > 1_000_000_000_000) impact = 2.5; // Large sell
  }

  const rawOut = params.amount * rate;
  
  // Apply Platform Fee (0.25%)
  const feeAmount = Math.floor(rawOut * (PLATFORM_FEE_BPS / 10000));
  const netOut = Math.floor(rawOut - feeAmount);

  // Apply Price Impact
  const impactedOut = Math.floor(netOut * (1 - (impact / 100)));

  // Simulate Route
  const protocols = ['Raydium', 'Orca', 'Meteora', 'Lifinity'];
  const primaryProto = protocols[Math.floor(Math.random() * protocols.length)];
  
  const routePlan = [{
      protocol: primaryProto,
      percent: 100
  }];

  return {
    inputMint: params.inputMint,
    inAmount: params.amount.toString(),
    outputMint: params.outputMint,
    outAmount: impactedOut.toString(),
    otherAmountThreshold: Math.floor(impactedOut * 0.99).toString(), // 1% slippage threshold from quote
    swapMode: 'ExactIn',
    slippageBps: params.slippageBps || 50,
    priceImpactPct: impact,
    routePlan,
    platformFee: {
        amount: feeAmount.toString(),
        feeBps: PLATFORM_FEE_BPS
    }
  };
};

// Simple cache for token list
let cachedTokens: JupiterToken[] = [];

/**
 * Fetches the verified token list from Jupiter
 */
export const getJupiterTokenList = async (): Promise<JupiterToken[]> => {
    if (cachedTokens.length > 0) return cachedTokens;

    try {
        const response = await fetch(JUPITER_TOKEN_LIST_URL);
        if (!response.ok) throw new Error("Failed to fetch Jupiter token list");
        const tokens = await response.json();
        
        // Inject HOT token if missing
        const hotToken = { 
            name: 'HOT NETWORK', 
            symbol: 'HOT', 
            address: 'HotDAO11111111111111111111111111111111111', 
            decimals: 9, 
            logoURI: '/assets/hot.png',
            chainId: 101,
            tags: ['governance']
        };
        
        if (!tokens.find((t: JupiterToken) => t.symbol === 'HOT')) {
            tokens.splice(1, 0, hotToken);
        }

        // Ensure SOL is at the top
        const solIndex = tokens.findIndex((t: JupiterToken) => t.address === SOL_MINT);
        if (solIndex > -1) {
            const sol = tokens.splice(solIndex, 1)[0];
            tokens.unshift(sol);
        }
        
        cachedTokens = tokens;
        return tokens;
    } catch (e) {
        console.warn("Using fallback token list due to API error", e);
        return [
            { address: SOL_MINT, chainId: 101, decimals: 9, name: 'Solana', symbol: 'SOL', logoURI: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png', tags: [] },
            { address: 'HotDAO11111111111111111111111111111111111', chainId: 101, decimals: 9, name: 'HOT NETWORK', symbol: 'HOT', logoURI: '/assets/hot.png', tags: ['governance'] },
            { address: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v', chainId: 101, decimals: 6, name: 'USD Coin', symbol: 'USDC', logoURI: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v/logo.png', tags: [] },
            { address: 'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263', chainId: 101, decimals: 5, name: 'Bonk', symbol: 'BONK', logoURI: 'https://arweave.net/hQiPZOsRZXGXBJd_82PhVdlM_hACsT_q6wqwf5cSY7I', tags: [] }
        ];
    }
};